import {
  ABORT_ERROR_NAME,
  HIDDEN_TAB_DISCONNECT_DELAY_MS,
  STREAM_CONNECTION_LEADER_LOCK,
} from "constants/streamConnection";
import { useEffect } from "react";
import { setUnreadMessages } from "store/slices/chatUnreadCount";
import { StreamChat } from "stream-chat";
import { useAppDispatch } from "./useAppDispatch";

const logConnection = (message: string) => {
  console.info(`[stream-connection] ${message}`);
};

export const useCloseStreamConnectionWhenHidden = (
  chatClient: StreamChat | null
): void => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!chatClient) return;

    let disconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let releaseLeaderLock: (() => void) | undefined;
    let isConnectionClosed = false;
    let isLeader = false;
    const leaderLockController = new AbortController();

    const closeConnection = () => {
      isConnectionClosed = true;
      logConnection("tab hidden too long, closing websocket");
      chatClient
        .closeConnection()
        .then(() => logConnection("websocket closed"))
        .catch((error) => console.error(error));
    };

    const reopenConnection = async () => {
      try {
        logConnection("reopening websocket");
        const response = await chatClient.openConnection();
        logConnection(
          `websocket reopened, unread count: ${response?.me?.total_unread_count}`
        );
        if (response?.me) {
          dispatch(setUnreadMessages(response.me.total_unread_count ?? 0));
        }
        await chatClient.recoverState();
        logConnection("channel state recovered");
      } catch (error) {
        console.error(error);
      }
    };

    const becomeLeader = () => {
      isLeader = true;
      clearTimeout(disconnectTimer);
      logConnection("tab is the connection leader, websocket stays open");

      if (isConnectionClosed) {
        isConnectionClosed = false;
        void reopenConnection();
      }

      return new Promise<void>((resolve) => {
        releaseLeaderLock = resolve;
      });
    };

    const handleLeaderLockError = (error: unknown) => {
      if (error instanceof DOMException && error.name === ABORT_ERROR_NAME) {
        return;
      }
      console.error(error);
    };

    const handleVisibilityChange = () => {
      clearTimeout(disconnectTimer);

      if (document.hidden) {
        if (isLeader) return;

        logConnection(
          `tab hidden, closing websocket in ${HIDDEN_TAB_DISCONNECT_DELAY_MS}ms`
        );
        disconnectTimer = setTimeout(
          closeConnection,
          HIDDEN_TAB_DISCONNECT_DELAY_MS
        );
        return;
      }

      if (!isConnectionClosed) {
        logConnection("tab visible before timeout, websocket kept open");
        return;
      }

      isConnectionClosed = false;
      void reopenConnection();
    };

    if ("locks" in navigator) {
      navigator.locks
        .request(
          STREAM_CONNECTION_LEADER_LOCK,
          { signal: leaderLockController.signal },
          becomeLeader
        )
        .catch(handleLeaderLockError);
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    handleVisibilityChange();

    return () => {
      clearTimeout(disconnectTimer);
      leaderLockController.abort();
      releaseLeaderLock?.();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [chatClient, dispatch]);
};
